import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AccessibilityPanel from "./AccessibilityPanel";
import { G_DARK, globalStyles } from "./Shared";

// The BOKU Core Facility page shell: sticky green bar, skip link, 1100px content column,
// footer, and the accessibility panel behind the button in the bar.
//
// Everything an individual app differs on is a prop, so this file can stay byte-identical
// across apps rather than being forked:
//
//   title          the page heading (24/700), rendered inside <main>
//   nav            navigation for the bar; a single-page tool passes nothing
//   right          anything after the accessibility button — a user menu, a sign-out
//   logoSrc        served from the app's public/ directory
//   home           where the wordmark links to
//   statementPath  route of this app's accessibility statement
//
// Below 768 px `nav` and `right` collapse behind a menu button (☰) in the bar and open as a
// panel under it, the wordmark keeps only the logo, and the accessibility button only its icon
// (ported from data-portal's phone layout, 2026-10-20). Nothing changes above 768 px: both
// wrappers are `display: contents` there, so the bar lays out exactly as before.
//
// Do not swap the Google Fonts <link> below for an @import inside an injected <style>:
// the fonts silently fail to load that way, and the page falls back to a system face at
// the same pixel size, which is easy to mistake for "the size is wrong".
export default function Layout({
  children,
  title = "",
  nav = null,
  right = null,
  logoSrc = "/csm_binf.png",
  home = "/",
  statementPath = "/accessibility",
}) {
  const [a11yOpen, setA11yOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const hasMenu = !!(nav || right);

  // Close the menu whenever the route changes — otherwise tapping a link leaves the
  // panel open on top of the page just asked for.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", fontFamily: "'DM Sans', sans-serif", color: "#1a1a1a" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap" rel="stylesheet" />
      <style>{globalStyles}</style>

      {/* Lets a keyboard user get past the bar rather than tabbing through it on every
          page (WCAG 2.4.1). Off-screen until it takes focus. */}
      <a
        href="#main-content"
        style={{
          position: "absolute", left: -9999, top: 0, zIndex: 300,
          background: "#fff", color: "#1a1a1a", padding: "10px 16px", borderRadius: 8,
          fontSize: 14, fontWeight: 600,
        }}
        onFocus={e => { e.target.style.left = "12px"; e.target.style.top = "12px"; }}
        onBlur={e => { e.target.style.left = "-9999px"; }}
      >
        Skip to main content
      </a>

      {/* Top bar. A <header> (the banner landmark), so the wordmark and the user menu are
          inside a landmark like everything else on the page (axe "region").

          The background is the dark accent: white text on the brand green #009640 is only
          3.86:1, below the 4.5:1 WCAG 1.4.3 needs at 14px; on #007a33 it is 5.5:1. In
          high-contrast and colour-blind-safe mode the variable is redefined as before.

          The row wraps instead of having a fixed height: at 200 % zoom (a 640 px viewport)
          or on a phone the nav and the user menu go to a second line rather than past the
          right edge (WCAG 1.4.10), and globalStyles stops it being sticky there. At full
          width everything fits on one 56px line, so nothing changes there. Found by the
          unio accessibility pass of 2026-10-04. */}
      <header data-topbar style={{ background: G_DARK, borderBottom: "3px solid rgba(0,0,0,0.25)", position: "sticky", top: 0, zIndex: 100 }}>
        <div data-topbar-inner style={{ maxWidth: 1100, margin: "0 auto", padding: "8px 24px", minHeight: 56, display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 24, rowGap: 8 }}>
          <Link to={home} style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", minWidth: 0 }}>
            <img src={logoSrc} alt="" style={{ height: 36, borderRadius: 6, flexShrink: 0 }} />
            <span data-org-name style={{ color: "#fff", fontSize: 14, fontWeight: 700, letterSpacing: "0.01em" }}>
              BOKU · Core Facility Bioinformatics
            </span>
          </Link>

          <div style={{ flex: 1 }} />

          {/* The menu's two halves. display: contents above the breakpoint, so they are not
              boxes of their own and the bar is what it was; below it globalStyles hides them
              until the menu button opens them, each a full-width row under the bar. */}
          {nav && <div id="topbar-menu-nav" data-nav data-open={menuOpen ? "true" : "false"} style={{ display: "contents" }}>
            {menuOpen && <div data-nav-org>BOKU · Core Facility Bioinformatics</div>}
            {nav}
          </div>}

          <div data-topbar-actions style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => setA11yOpen(true)}
              aria-label="Accessibility settings"
              title="Accessibility settings"
              style={{ background: "rgba(0,0,0,0.18)", border: "none", color: "#fff", fontSize: 13, fontWeight: 500, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <span aria-hidden="true">♿</span><span data-a11y-label> Accessibility</span>
            </button>
            {/* Hidden above 768 px by globalStyles. A real button with aria-expanded, so
                the state is announced rather than merely looking right. */}
            {hasMenu && (
              <button
                type="button"
                data-nav-toggle
                onClick={() => setMenuOpen(o => !o)}
                aria-expanded={menuOpen}
                aria-controls={[nav && "topbar-menu-nav", right && "topbar-menu-right"].filter(Boolean).join(" ")}
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                style={{
                  display: "none", alignItems: "center", justifyContent: "center",
                  background: "rgba(0,0,0,0.18)", border: "none", color: "#fff",
                  fontSize: 18, lineHeight: 1, width: 44, height: 44, borderRadius: 8,
                  cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
                }}
              >
                <span aria-hidden="true">{menuOpen ? "✕" : "☰"}</span>
              </button>
            )}
            {right && <div id="topbar-menu-right" data-nav-right data-open={menuOpen ? "true" : "false"} style={{ display: "contents" }}>
              {right}
            </div>}
          </div>
        </div>
      </header>

      {/* Page content */}
      <main id="main-content" data-content style={{ maxWidth: 1100, margin: "0 auto", padding: "36px 24px 80px" }}>
        {/* tabIndex -1: not a Tab stop, but an app can move focus here after a route change,
            so a screen reader starts reading the new page instead of staying on <body>. */}
        {title && <h1 tabIndex={-1} style={{ margin: "0 0 18px", fontSize: 24, fontWeight: 700, outline: "none" }}>{title}</h1>}
        {children}
      </main>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid #e5e7eb", background: "#fff", padding: "14px 24px", textAlign: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 12 }}>
          BOKU Core Facility Bioinformatics · University of Natural Resources and Life Sciences, Vienna
          {" · "}
          <Link to={statementPath} style={{ color: G_DARK }}>Accessibility</Link>
        </span>
      </footer>

      {a11yOpen && <AccessibilityPanel onClose={() => setA11yOpen(false)} statementPath={statementPath} />}
    </div>
  );
}
